import React, { useState, useEffect } from 'react';
import { BrainCircuit, TrendingUp, AlertTriangle, CheckSquare, Clock, X, CheckCircle, Loader2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import api from '../../services/api';

const AiInsights = () => {
    const { tasks } = useAppContext();
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dismissedInsights, setDismissedInsights] = useState([]);
    const [appliedFixes, setAppliedFixes] = useState([]);
    const [showRiskModal, setShowRiskModal] = useState(false);

    useEffect(() => {
        const loadInsights = async () => {
            try {
                setLoading(true);
                setError(null);
                const { data } = await api.get('/ai/risk-scan');
                if (data && data.insights) {
                    // Expect AI to return [{ taskId, riskScore, recommendation }]
                    setInsights(data.insights);
                } else {
                    setInsights([]);
                }
            } catch (err) {
                console.error("Failed to load insights:", err);
                setError("Ollama AI could not generate insights at this time. Please ensure the local llama3 model is running and reachable.");
            } finally {
                setLoading(false);
            }
        };
        loadInsights();
    }, []);

    // Calculate dynamic stats
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'completed').length;
    const healthPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 100;
    
    // Check risks dynamically
    const highRiskTasks = insights.filter(i => i.riskScore > 75);

    const handleDismiss = (id) => setDismissedInsights(prev => [...prev, id]);

    const handleApplyFix = (id) => {
        setAppliedFixes(prev => [...prev, id]);
        setTimeout(() => handleDismiss(id), 2000); // Auto dismiss after 2s showing success
    };

    const isDismissed = (id) => dismissedInsights.includes(id);
    const isApplied = (id) => appliedFixes.includes(id);

    const getTaskData = (taskId) => {
        return tasks.find(t => t.id === taskId || t._id === taskId);
    };

    if (loading) {
        return (
            <div className="flex flex-col h-full bg-gray-50 pb-10 items-center justify-center pt-24">
                <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
                <h2 className="text-xl font-bold text-gray-900">Ollama is analyzing your team...</h2>
                <p className="text-gray-500 mt-2 text-sm text-center max-w-md">
                    The Agentic AI is evaluating workload balance and predicting deadline risks based on current capacity constraints. This typically takes a few moments.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-gray-50 pb-10">
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">AI Insights</h1>
                    <p className="mt-1 text-sm text-gray-500">Intelligent analysis and actionable recommendations generated by LLaMA 3.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Overall Health Block */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-purple-50 rounded-lg">
                            <BrainCircuit className="h-6 w-6 text-purple-600" />
                        </div>
                        <span className="text-sm font-medium text-purple-600 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100">
                            {error ? 'System Error' : 'System Active'}
                        </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Overall Task Health</h3>
                    <p className="text-sm text-gray-500 mb-4">Your current sprint completion tracking stands at {healthPercent}%.</p>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${healthPercent}%` }}></div>
                    </div>
                </div>

                {/* Velocity Indicator */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-50 rounded-lg">
                            <TrendingUp className="h-6 w-6 text-green-600" />
                        </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Total Productivity</h3>
                    <p className="text-sm text-gray-500 mb-4">Your team has successfully cleared {completedTasks} out of {totalTasks} total tasks.</p>
                    <div className="text-green-600 font-bold text-2xl">{completedTasks} Completed</div>
                </div>

                {/* Risk Factors Container */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${highRiskTasks.length > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                            <AlertTriangle className={`h-6 w-6 ${highRiskTasks.length > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                        </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Risk Factors</h3>
                    <p className="text-sm text-gray-500 mb-4">
                        {highRiskTasks.length > 0 
                            ? `${highRiskTasks.length} task(s) currently face a high probability of missing their deadline.`
                            : `Ollama detected no immediate critical risk factors.`}
                    </p>
                    
                    {highRiskTasks.length > 0 && (
                        <button
                            onClick={() => setShowRiskModal(true)}
                            className="mt-auto text-sm font-medium text-red-600 hover:text-red-800 text-left w-max"
                        >
                            Review Risks &rarr;
                        </button>
                    )}
                </div>
            </div>

            {/* AI Error Fallback */}
            {error && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-8 text-red-700 text-sm">
                    {error}
                </div>
            )}

            {/* Recommended Insights Generated dynamically */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <BrainCircuit className="h-5 w-5 mr-2 text-purple-500" />
                        Recommended Actions
                    </h3>
                </div>
                <div className="divide-y divide-gray-100">
                    {!error && insights.length === 0 && (
                        <div className="p-10 text-center flex flex-col items-center justify-center">
                            <CheckCircle className="h-10 w-10 text-green-400 mb-3" />
                            <h4 className="text-base font-medium text-gray-900">All caught up!</h4>
                            <p className="text-sm text-gray-500 mt-1">Ollama found no critical workload overlaps or major optimizations required right now.</p>
                        </div>
                    )}

                    {!error && insights.map((insight, idx) => {
                        const insightId = `insight-${idx}`;
                        if (isDismissed(insightId)) return null;

                        const task = getTaskData(insight.taskId);
                        const taskTitle = task ? task.title : 'Deleted or Unknown Task';

                        return (
                            <div key={insightId} className={`p-6 hover:bg-gray-50 transition-colors flex gap-4 ${isApplied(insightId) ? 'bg-green-50/50' : ''}`}>
                                <div className="mt-1 flex-shrink-0">
                                    {isApplied(insightId) ? (
                                        <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                        </div>
                                    ) : (
                                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${insight.riskScore > 70 ? 'bg-amber-100' : 'bg-blue-100'}`}>
                                            <Clock className={`h-4 w-4 ${insight.riskScore > 70 ? 'text-amber-600' : 'text-blue-600'}`} />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-gray-900">Review "{taskTitle}"</h4>
                                    <p className="text-sm text-gray-500 mt-1">{insight.recommendation}</p>
                                    <div className="mt-3 flex gap-3">
                                        {isApplied(insightId) ? (
                                            <span className="text-xs font-medium text-green-700 flex items-center">
                                                <CheckCircle className="h-3 w-3 mr-1" /> Action acknowledged
                                            </span>
                                        ) : (
                                            <>
                                                <button onClick={() => handleApplyFix(insightId)} className="text-xs font-medium bg-blue-50 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-100 transition-colors">Acknowledge</button>
                                                <button onClick={() => handleDismiss(insightId)} className="text-xs font-medium text-gray-500 hover:text-gray-700">Dismiss</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Risk Modal completely wired to live backend data */}
                {showRiskModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
                            <div className="flex justify-between items-center p-6 border-b border-gray-100">
                                <h2 className="text-xl font-bold flex items-center text-gray-900">
                                    <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                                    Risk Analysis Report
                                </h2>
                                <button onClick={() => setShowRiskModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="p-6">
                                <p className="text-sm text-gray-600 mb-4">
                                    NeuroTask AI has identified the following tasks as having a high probability (&gt;75%) of missing their intended deadlines based on assignee workload mapping.
                                </p>
                                <div className="space-y-3 mb-6 max-h-[60vh] overflow-y-auto">
                                    {highRiskTasks.map((insight, idx) => {
                                        const tData = getTaskData(insight.taskId);
                                        const isVeryHigh = insight.riskScore >= 90;
                                        return (
                                            <div key={idx} className={`p-3 border rounded-lg flex justify-between items-center ${isVeryHigh ? 'border-red-100 bg-red-50' : 'border-amber-100 bg-amber-50'}`}>
                                                <div>
                                                    <h4 className="font-medium text-gray-900 text-sm">{tData ? tData.title : 'Deleted Task'}</h4>
                                                    <p className="text-xs text-gray-500 mt-0.5">Assigned to: {tData ? (tData.assigneeId?.name || tData.assignee?.name || 'Unassigned') : 'Unknown'}</p>
                                                </div>
                                                <span className={`text-xs font-bold uppercase whitespace-nowrap ml-2 ${isVeryHigh ? 'text-red-600' : 'text-amber-600'}`}>
                                                    {insight.riskScore}% Risk
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button onClick={() => setShowRiskModal(false)} className="px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50">
                                        Close panel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AiInsights;
